import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useForm } from 'react-hook-form';
import type { UseFormReturn } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod/v4';
import { toast } from 'sonner';
import { getApiErrorDetail } from '@hetivo/hetivo-lib-front-utils';
import type { Location } from '@hetivo/hetivo-lib-types';
import { LOCATION_STATUS } from '@hetivo/hetivo-lib-types';
import type { LocationStatus } from '@hetivo/hetivo-lib-types';

import { normalizeCountryCode } from '../constants';

import type { UpdateLocationFormValues } from '../schemas/update-location.schema';
import { hasAddressData, hasPhoneData, normaliseGeocodingScore, toGeoCoordinates } from '../utils/location-form.utils';
import { useUpdateLocation } from './use-update-location';

// ─── Types ────────────────────────────────────────────────────────────────────

export type { UpdateLocationFormValues };

export interface UseUpdateLocationFormReturn {
  form: UseFormReturn<UpdateLocationFormValues>;
  isDirty: boolean;
  isPending: boolean;
  onCancel: () => void;
  onSubmit: (data: UpdateLocationFormValues) => void;
  resetKey: number;
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

function toAddressDefaults(address: Location['address']): UpdateLocationFormValues['address'] {
  const defaults: UpdateLocationFormValues['address'] = {
    additionalInformations: '',
    city: '',
    complement: '',
    country: '',
    digicode: '',
    floor: '',
    hasElevator: false,
    houseNumber: '',
    postalCode: '',
    street: '',
    title: '',
  };
  const result = Object.assign(defaults, address) as UpdateLocationFormValues['address'];
  result.country = normalizeCountryCode(result.country);
  return result;
}

function toDefaultValues(location: Location): UpdateLocationFormValues {
  const geocodingScore = location.geocodingScore !== undefined ? location.geocodingScore * 100 : 0;
  return {
    address: toAddressDefaults(location.address),
    color: location.color,
    coordinates: { x: location.coordinates?.x, y: location.coordinates?.y },
    customerSupportPhone: location.customerSupportPhone ?? null,
    geocodingScore,
    geocodingSource: location.geocodingSource,
    name: location.name,
    phone: location.phone ?? null,
    status: location.status,
    territoryId: location.territoryId,
  };
}

// ─── Constants ────────────────────────────────────────────────────────────────

const phoneSchema = z
  .object({ dialCode: z.string(), number: z.string() })
  .refine((p) => /^\d{4,15}$/.test(p.number), { message: 'Phone number must contain 4 to 15 digits' })
  .nullable();

// ─── Hook ─────────────────────────────────────────────────────────────────────

export function useUpdateLocationForm(location: Location): UseUpdateLocationFormReturn {
  const { t } = useTranslation('settings');
  const updateLocation = useUpdateLocation(location.id);

  const schema = useMemo(
    () =>
      z.object({
        address: z.object({
          additionalInformations: z.string(),
          city: z.string(),
          complement: z.string(),
          country: z.string(),
          digicode: z.string(),
          floor: z.string(),
          hasElevator: z.boolean(),
          houseNumber: z.string(),
          postalCode: z.string(),
          street: z.string(),
          title: z.string(),
        }),
        color: z.string().min(1, t('locations.settings.validation.colorRequired')),
        coordinates: z.object({
          x: z.number().optional(),
          y: z.number().optional(),
        }),
        customerSupportPhone: phoneSchema,
        geocodingScore: z.number().min(1, t('locations.create.validation.geocodingRequired')),
        geocodingSource: z.enum(['autocomplete', 'manual']).optional(),
        name: z.string().trim().min(1, t('locations.settings.validation.nameRequired')),
        phone: phoneSchema,
        status: z.enum(Object.values(LOCATION_STATUS) as [LocationStatus, ...LocationStatus[]]),
        territoryId: z.string(),
      }),
    [t],
  );

  const form = useForm<UpdateLocationFormValues>({
    defaultValues: toDefaultValues(location),
    mode: 'all',
    resolver: zodResolver(schema),
  });

  // Reset form when location data actually changes on the server (after successful update + refetch).
  const { reset } = form;
  const locationRef = useRef(location);
  locationRef.current = location;

  useEffect(() => {
    reset(toDefaultValues(locationRef.current));
  }, [location.updatedAt, reset]);

  // ─── Submit ───────────────────────────────────────────────────────────────

  const onSubmit = useCallback(
    (data: UpdateLocationFormValues): void => {
      const geocodingScore = normaliseGeocodingScore(data.geocodingScore);
      const coordinates = toGeoCoordinates(data.coordinates);

      updateLocation
        .mutateAsync({
          ...(hasAddressData(data.address) ? { address: data.address } : {}),
          color: data.color,
          ...(coordinates !== undefined ? { coordinates } : {}),
          ...(hasPhoneData(data.customerSupportPhone)
            ? { customerSupportPhone: data.customerSupportPhone }
            : { customerSupportPhone: null }),
          ...(geocodingScore !== undefined ? { geocodingScore } : {}),
          geocodingSource: data.geocodingSource,
          name: data.name,
          ...(hasPhoneData(data.phone) ? { phone: data.phone } : { phone: null }),
          status: data.status,
        })
        .then(
          () => {
            toast.success(t('locations.settings.updateSuccess'));
          },
          (error: unknown) => {
            const detail = getApiErrorDetail(error);
            if (detail.statusCode === 409) {
              if (detail.errorCode === 'Conflict.DuplicateLocationName') {
                form.setError('name', { message: t('locations.settings.validation.nameExists') });
              }
              return;
            }
            toast.error(detail.message || t('locations.settings.updateError'));
          },
        );
    },
    [form, t, updateLocation],
  );

  const [resetKey, setResetKey] = useState(0);

  const onCancel = useCallback((): void => {
    form.reset();
    setResetKey((k) => k + 1);
  }, [form]);

  return {
    form,
    isDirty: form.formState.isDirty,
    isPending: updateLocation.isPending,
    onCancel,
    onSubmit,
    resetKey,
  };
}
